import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { environment, Environment } from '@toni/util'

interface User {
  simulateContentPass: boolean,
  hasContentPass?: boolean
}

interface ContentPassStatus {
  isInitialized: boolean,
  isUser: boolean
}

interface ContentPassUser {
  isLoggedIn(): boolean
}

interface ContentPassApi {
  (action: 'authenticate', callback: (err: any, user: ContentPassUser) => void): void
}

declare global {
  interface Window {
    cp?: ContentPassApi
  }
}

export const useSessionStore = defineStore('session', () => {
  // State
  const user = ref<User>({
    simulateContentPass: false, // Default value for simulate content pass flag
    hasContentPass: undefined // Default value for Content Pass flag
  })

  const contentPassStatus = ref<ContentPassStatus>({
    isInitialized: false,
    isUser: false
  })

  // Getters
  const simulateContentPass = computed((): boolean => {
    return !!user.value?.simulateContentPass
  })

  const hasContentPass = computed((): boolean | undefined => {
    return user.value?.hasContentPass
  })

  const simulateContentPassOrContentPass = computed((): boolean | undefined => {
    return simulateContentPass.value || hasContentPass.value
  })

  // Actions
  const storeUser = (newUser: User): void => {
    user.value = newUser
  }

  // only needed as solution to simulate a content pass subscription by setting a url parameter
  const updateSimulateContentPassParamFromUrl = (): void => {
    const urlParams = new URLSearchParams(window.location.search)
    const simulateContentPassParam = urlParams.get('simulateContentPass')
    if (simulateContentPassParam === 'true' && environment !== Environment.production) {
      user.value.simulateContentPass = true
    }
  }

  const updateContentPassStatus = (): void => {
    if (window.cp) {
      window.cp('authenticate', (err: any, cpUser: ContentPassUser) => {
        if (!err && cpUser.isLoggedIn()) {
          user.value.hasContentPass = true
        } else {
          user.value.hasContentPass = false
        }
      })
    }
  }

  const initContentPassStatus = (): void => {
    if (!import.meta.env.SSR) {
      // Initial check
      updateContentPassStatus()

      // Listen for ContentPass events
      window.addEventListener('cp:login', () => {
        user.value.hasContentPass = true
      })

      window.addEventListener('cp:logout', () => {
        user.value.hasContentPass = false
      })
    }
  }

  const initializeContentPass = async (): Promise<void> => {
    if (typeof window !== 'undefined' && window.cp) {
      window.cp('authenticate', (err: any, _cpUser: ContentPassUser) => {
        contentPassStatus.value.isInitialized = true
        contentPassStatus.value.isUser = !err
      })
    }
  }

  return {
    user,
    simulateContentPass,
    hasContentPass,
    simulateContentPassOrContentPass,
    storeUser,
    updateSimulateContentPassParamFromUrl,
    updateContentPassStatus,
    initContentPassStatus,
    initializeContentPass,
    contentPassStatus
  }
})
